import { action, makeObservable, observable } from 'mobx';
import { PageState } from '../PageState';
import { addResizeObserver } from '../resizeObserverCompat';
import { CollectionPageStateBIReporter } from './CollectionPageStateBIReporter';
import { ReportBI } from '../../services';
import { ToolbarCollectionState } from './ToolbarCollectionState';
import throttle from 'lodash/throttle';

export const NARROW_PAGE_BREAKPOINT = 960;
export const SCROLL_RESTORE_WINDOW_MS = 3000;

export interface CollectionPageStateParams {
  pageTitle?: string;
  hasHeader?: boolean;
  hideHeaderTotal?: boolean;
  reportBI?: ReportBI;
  withinRouter?: boolean;
}

export class CollectionPageState {
  pageTitle?: string;
  hasHeader?: boolean = false;
  total?: number | boolean = false;
  hideHeaderTotal?: boolean = false;
  scrollTopBeforeUnmount: number | null = null;
  withinRouter?: boolean;
  isNarrow = false;
  readonly startTime = performance.now();
  readonly bi?: CollectionPageStateBIReporter;

  readonly pageState = new PageState();

  primaryToolbar: ToolbarCollectionState | null = null;

  constructor(params: CollectionPageStateParams) {
    this.pageTitle = params.pageTitle;
    this.hasHeader = params.hasHeader;
    this.hideHeaderTotal = params.hideHeaderTotal;
    this.withinRouter = params.withinRouter;
    if (params.reportBI) {
      this.bi = new CollectionPageStateBIReporter({
        reportBI: params.reportBI,
        collectionPageState: this,
      });
    }
    makeObservable(this, {
      pageTitle: observable.ref,
      hasHeader: observable.ref,
      hideHeaderTotal: observable.ref,
      total: observable.ref,
      primaryToolbar: observable.ref,
      isNarrow: observable.ref,
      _updateIsNarrow: action.bound,
    });
  }

  _updateIsNarrow() {
    const el = this.pageState.stackingRootRef.current;
    this.isNarrow = el ? el.clientWidth < NARROW_PAGE_BREAKPOINT : false;
  }

  init() {
    this.bi?.init();

    const scrollableContent = this.pageState.scrollableContentRef.current;
    if (!scrollableContent) {
      return () => {};
    }

    const restoreDeadline = performance.now() + SCROLL_RESTORE_WINDOW_MS;
    const restoreScrollTop = () => {
      if (this.scrollTopBeforeUnmount === null) {
        return;
      }
      // The offset belongs to one trip back. Leaving it armed longer could let
      // unrelated content growth trigger a surprise jump.
      if (performance.now() > restoreDeadline) {
        this.scrollTopBeforeUnmount = null;
        return;
      }
      const maxScrollTop =
        scrollableContent.scrollHeight - scrollableContent.clientHeight;
      if (maxScrollTop >= this.scrollTopBeforeUnmount) {
        scrollableContent.scrollTo({
          top: this.scrollTopBeforeUnmount,
        });
        this.scrollTopBeforeUnmount = null;
      }
    };

    // The scrollable viewport keeps a fixed height, so it never resizes as rows
    // load — only its content does. Observe both.
    const scrollDisposer = addResizeObserver(
      scrollableContent,
      restoreScrollTop,
    );
    // This is design-system Page content; a dedicated ref would be preferable,
    // but none is exposed. A missing element or DOM change degrades silently.
    const contentDisposer = addResizeObserver(
      scrollableContent.firstElementChild,
      restoreScrollTop,
    );

    this._updateIsNarrow();
    const narrowDisposer = addResizeObserver(
      this.pageState.stackingRootRef.current,
      throttle(this._updateIsNarrow, 100),
    );

    this.bi?.appLoaded?.();

    return () => {
      scrollDisposer?.();
      contentDisposer?.();
      narrowDisposer?.();
    };
  }
}
